import { Form, Select, Typography, Divider } from "antd";
import { useCallback, useMemo, useState } from "react";
import { DONATION_CATEGORIES } from "./categoryData";
import { Button, CloseButton, Modal, Spinner } from "react-bootstrap";

const { Text } = Typography;

export const CreateNewPurposeModal = ({
  open,
  setShowAddModal,
  addDonationPurpose,
  existingPurposes = [],
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // filter out already added codes
  const availableCategories = useMemo(() => {
    const existingCodes = existingPurposes.map((p) => p.code);

    return DONATION_CATEGORIES.filter(
      (category) => !existingCodes.includes(category.value),
    );
  }, [existingPurposes]);

  const noMoreCategories = availableCategories.length === 0;

  const selectedCode = Form.useWatch("code", form);

  const selectedCategory = DONATION_CATEGORIES.find(
    (c) => c.value === selectedCode,
  );

  const handleClose = useCallback(() => {
    setShowAddModal(false);
  }, [setShowAddModal]);

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      await addDonationPurpose({
        code: values.code,
        name: selectedCategory.label,
        description: selectedCategory.description,
        active: true,
      });

      form.resetFields();
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={open} onHide={handleClose} keyboard={false}>
      <Modal.Header className="bg-body-tertiarydd border-0">
        <Modal.Title style={{ fontSize: 14 }} className="ps-2">
          Create donation credit purpose
        </Modal.Title>
        <CloseButton
          className="btn btn-circle btn-sm transition-base p-0"
          onClick={handleClose}
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-0 pb-3">
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="code"
            label="Category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select
              placeholder={
                noMoreCategories
                  ? "All categories have been added"
                  : "Select category"
              }
              options={availableCategories}
              showSearch={{
                optionFilterProp: "label",
              }}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              disabled={noMoreCategories}
              notFoundContent={
                noMoreCategories
                  ? "No categories left to add"
                  : "No results found"
              }
            />
          </Form.Item>

          {selectedCategory && (
            <>
              <div
                style={{ background: "#F8FAFC", padding: 12, borderRadius: 4 }}
                className="custom-shadow"
              >
                <Text strong style={{ fontFamily: "Inter" }}>
                  {selectedCategory.label}
                </Text>
                <br />
                <Text
                  type="secondary"
                  style={{ fontSize: 13, fontFamily: "Inter" }}
                >
                  {selectedCategory.description}
                </Text>
              </div>
            </>
          )}

          {noMoreCategories && (
            <div
              style={{
                background: "#F3F4F6",
                padding: 12,
                borderRadius: 8,
                fontSize: 13,
                color: "#6B7280",
              }}
            >
              All donation purposes have already been created.
            </div>
          )}

          <div className="d-flex gap-2 mt-4">
            <Button
              size="md"
              variant="secondary"
              className="text-xs"
              onClick={() => setShowAddModal(false)}
            >
              Close
            </Button>
            {submitting ? (
              <Spinner animation="border" size="md" variant="success" />
            ) : (
              <Button
                size="md"
                variant="success"
                className="text-xs"
                type="submit"
                disabled={noMoreCategories}
              >
                Save
              </Button>
            )}
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
